import {
  Checkbox,
  CheckboxGroupBaseProps,
  CheckboxRootBaseProps,
} from "@ark-ui/react";
import { type FieldInputProps } from "@ark-ui/react";
import {
  aromaCheckbox,
  AromaCheckboxVariantProps,
} from "@zenchef/styled-system/recipes";
import { FieldProps } from "../../utils/types";
import { Text } from "../typography/Text";
import Icon from "../Icon/Icon";
import { Flex } from "@zenchef/styled-system/jsx";
import withTestId from "../../utils/withTestId";
import React, { PropsWithChildren, ReactElement, useContext } from "react";
import CheckboxContext from "./CheckboxContext";

export interface CheckboxProps
  extends AromaCheckboxVariantProps,
    Omit<
      FieldInputProps,
      "size" | "checked" | "value" | "defaultChecked" | "onChange"
    >,
    CheckboxRootBaseProps,
    FieldProps {
  onChange?: (status: Checkbox.CheckedState) => void;
  title: string;
  subtitle?: string;
  invalid?: boolean;
  errorText?: string;
}

const AromaCheckbox = ({
  title,
  subtitle,
  checked: checkedProp,
  value,
  required,
  invalid,
  errorText,
  disabled,
  onChange,
  ...propsWithVariants
}: CheckboxProps) => {
  const { isGrouped } = useContext(CheckboxContext);

  const size = isGrouped ? "medium" : undefined;

  const [variants] = aromaCheckbox.splitVariantProps({
    ...propsWithVariants,
    size,
  });
  const classes = aromaCheckbox(variants);

  return (
    <Checkbox.Root
      checked={checkedProp}
      value={value}
      disabled={disabled}
      required={required}
      invalid={invalid}
      onCheckedChange={({ checked }) => onChange?.(checked)}
    >
      <Flex className={classes.container}>
        <Checkbox.Control className={classes.control}>
          <Checkbox.Indicator className={classes.icon}>
            <Icon name="check-01" />
          </Checkbox.Indicator>
          <Checkbox.Indicator indeterminate className={classes.icon}>
            <Icon name="minus" />
          </Checkbox.Indicator>
        </Checkbox.Control>
        <Checkbox.Label className={classes.label}>
          <Text className={classes.title}>{title}</Text>
          {invalid ? (
            <Flex className={classes.errorContainer}>
              <Icon name="alert-triangle" />
              <Text className={classes.errorText}>{errorText}</Text>
            </Flex>
          ) : (
            <>
              {subtitle ? (
                <Text className={classes.subtitle}>{subtitle}</Text>
              ) : null}
            </>
          )}
        </Checkbox.Label>
        <Checkbox.HiddenInput />
      </Flex>
    </Checkbox.Root>
  );
};
export interface CheckboxGroupProps
  extends AromaCheckboxVariantProps,
    CheckboxGroupBaseProps,
    PropsWithChildren,
    FieldProps {
  title?: string;
  subtitle?: string;
  className?: string;
}

const AromaCheckboxGroup = ({
  title,
  subtitle,
  defaultValue,
  value,
  children,
  className,
  onValueChange,
  ...propsWithVariants
}: CheckboxGroupProps) => {
  const areValidComponents = React.Children.toArray(children).every(
    isCheckboxItemComponent,
  );

  if (!areValidComponents) {
    console.error(
      "CheckboxGroup only accepts Checkbox.Item components as children",
    );
    return null;
  }

  const [variants] = aromaCheckbox.splitVariantProps(propsWithVariants);
  const classes = aromaCheckbox(variants);

  const hasTitleOrSubtitle = !!(title ?? subtitle);

  return (
    <CheckboxContext.Provider value={{ isGrouped: true }}>
      <Checkbox.Group
        defaultValue={defaultValue}
        value={value}
        onValueChange={onValueChange}
        className={className}
      >
        {hasTitleOrSubtitle ? (
          <Flex flexDirection="column" marginBottom="margin.4">
            {title ? <Text className={classes.title}>{title}</Text> : null}
            {subtitle ? (
              <Text className={classes.subtitle}>{subtitle}</Text>
            ) : null}
          </Flex>
        ) : null}
        <Flex flexDirection="column" gap="gap.1">
          {children}
        </Flex>
      </Checkbox.Group>
    </CheckboxContext.Provider>
  );
};

function isCheckboxItemComponent(
  component: React.ReactNode,
): component is ReactElement<CheckboxProps, typeof TempAromaCheckbox.Item> {
  return (
    React.isValidElement(component) && component.type === TempAromaCheckbox.Item
  );
}

const TempAromaCheckbox = () => <></>;

TempAromaCheckbox.Group = AromaCheckboxGroup;
TempAromaCheckbox.Item = withTestId("checkbox")(AromaCheckbox);

export default TempAromaCheckbox;
